import {
  DEFAULT_ACCENT_COLOR,
  DEFAULT_PORTAL_URL,
  DEFAULT_SWARM_API_URL,
  PROJECT_SCHEMA_VERSION,
  SWARM_DEFAULTS_REVISION,
  type AudioStrip,
  type ChannelSettings,
  type Id,
  type Project,
} from '../core/model';
import { defaultCaptions } from '../core/captions';
import { defaultClips, defaultFeatured, defaultRaids } from '../core/channel';

/** 64 random bits: collision-safe for any realistic document, short enough to read in dumps. */
export function createId(): Id {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

export function defaultAudioStrip(): AudioStrip {
  return { volume: 1, muted: false, balance: 0, forceMono: false, syncOffsetMs: 0, monitoring: 'off' };
}

export function defaultChannelSettings(): ChannelSettings {
  return {
    info: { title: '', goLiveNotification: '', category: '', tags: [], language: '', contentLabels: [], rerun: false },
    profile: { displayName: '', bio: '', links: [], accentColor: DEFAULT_ACCENT_COLOR, avatarUrl: '', bannerUrl: '', offlineImageUrl: '' },
    schedule: [],
    featured: defaultFeatured(),
    goal: null,
    raids: defaultRaids(),
    clips: defaultClips(),
    favourites: [],
    vodRetentionDays: 0,
    saveBroadcasts: false,
    disconnectProtection: false,
    portalUrl: DEFAULT_PORTAL_URL,
  };
}

export function createDefaultProject(name = 'Untitled'): Project {
  const now = Date.now();
  const sceneId = createId();
  const cutId = createId();
  const fadeId = createId();
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: createId(),
    name,
    createdAt: now,
    updatedAt: now,
    video: { width: 1920, height: 1080, fps: 30 },
    audio: { sampleRate: 48000, masterVolume: 1, masterMuted: false, monitorDeviceId: null },
    output: {
      streaming: {
        mode: 'p2p',
        signalingUrl: 'ws://localhost:8787',
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        iceTransportPolicy: 'all',
        maxViewers: 4,
        allowGuests: false,
        guestLink: false,
        guestApproval: 'lobby',
        videoKbps: 4500,
        audioKbps: 128,
        codec: 'auto',
        degradation: 'balanced',
        whipEndpoint: '',
        whipBearerToken: '',
        swarmApiUrl: DEFAULT_SWARM_API_URL,
        swarmStreamKey: '',
        swarmSegmentMs: 200,
        swarmDefaultsRevision: SWARM_DEFAULTS_REVISION,
        swarmH264Fallback: 'auto',
        captions: defaultCaptions(),
      },
      recording: { mimeType: 'auto', videoKbps: 8000, audioKbps: 160, sink: 'opfs' },
    },
    channel: defaultChannelSettings(),
    sources: {},
    scenes: { [sceneId]: { id: sceneId, name: 'Scene', items: [], filters: [] } },
    sceneOrder: [sceneId],
    currentSceneId: sceneId,
    transitions: {
      [cutId]: { id: cutId, kind: 'cut', name: 'Cut', settings: {} },
      [fadeId]: { id: fadeId, kind: 'fade', name: 'Fade', settings: {} },
    },
    transitionOrder: [cutId, fadeId],
    currentTransitionId: fadeId,
    transitionDurationMs: 300,
    hotkeys: {},
    plugins: [],
    pluginData: {},
  };
}
